import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useQuery } from 'react-query';

import SelectMulti from 'app/components/Select/SelectMulti';
import { QUERIES } from 'app/components/Table/core/constants';
import Typography from 'app/components/Typography';

import {
  DEFAULT_LOCATION_FILTER,
  LOCATION_OPTIONS,
  RACK_OPTIONS,
  SELECTED_LOCATION,
  SELECTED_RACK,
} from 'app/modules/inventory/items/core/constants';
import { LocationFilterParams } from 'app/modules/inventory/items/core/type';
import { getRacks2 } from 'app/modules/inventory/racks/core/requests';
import { Body, Header, Main } from 'app/modules/inventory/stock-run/components/StockRunFilter';
import { getWards } from 'app/modules/system-settings/locations/wards/core/requests';

import { useOrganizationStore } from 'theme/layout/components/aside/store';

interface LocationFilterProps {
  openFilter: boolean;
  setIsOpenFilter: (openFilter: boolean) => void;
  onApplyFilter?: (filter: LocationFilterParams) => void;
  currentFilter?: LocationFilterParams;
}

const LocationFilter = ({
  openFilter,
  setIsOpenFilter,
  onApplyFilter,
  currentFilter,
}: LocationFilterProps) => {
  const organizationId = useOrganizationStore.getState().state.organization?.id;

  const [filter, setFilter] = useState<LocationFilterParams>(
    currentFilter || DEFAULT_LOCATION_FILTER,
  );
  const [menuIsOpen, setMenuIsOpen] = useState<{
    location: boolean;
    rack: boolean;
  }>({
    location: false,
    rack: false,
  });

  useEffect(() => {
    if (currentFilter) setFilter(currentFilter);
  }, [currentFilter]);

  const { data: wards = [], isFetching: isFetchingWards } = useQuery({
    queryKey: [QUERIES.WARDS_LIST],
    queryFn: () => getWards(`page=1&limit=9999&orgId=${organizationId}`),
    select: (data) => data.data?.items,
    refetchOnWindowFocus: false,
  });

  const { data: racks = [], isFetching: isFetchingRacks } = useQuery({
    queryKey: [QUERIES.RACK_LIST],
    queryFn: () => getRacks2(`page=1&limit=9999&orgId=${organizationId}`),
    select: (data) => data.data?.items,
    refetchOnWindowFocus: false,
  });

  const handleCloseFilter = () => setIsOpenFilter(false);

  const handleApplyFilter = () => {
    if (onApplyFilter) {
      onApplyFilter(filter);
    }
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setFilter(DEFAULT_LOCATION_FILTER);
    if (onApplyFilter) {
      onApplyFilter(DEFAULT_LOCATION_FILTER);
    }
    handleCloseFilter();
  };

  const handleGetKey = (key: keyof LocationFilterParams) => {
    if (key === 'locationIds') return 'location';
    if (key === 'rackIds') return 'rack';
    return key;
  };

  const handleSelectChange = useCallback(
    (key: keyof LocationFilterParams) => (newValue: { value: string }[]) => {
      setFilter((prev) => ({
        ...prev,
        [key]: newValue.map((el) => String(el.value)),
      }));
      setMenuIsOpen((prev) => ({
        ...prev,
        [handleGetKey(key)]: true,
      }));
    },
    [],
  );

  const handleMenuClose = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: false });

  const handleMenuOpen = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: true });

  return (
    <Main>
      <Header>
        <Typography variant="heading-6">Filter</Typography>
        <Typography
          className="clear_all cursor-pointer"
          variant="body-5"
          onClick={handleClearAll}
          color="primary"
        >
          Clear all
        </Typography>
      </Header>
      <Body>
        <Typography variant="body-7" color="gray-700" className="mb-2">
          Location
        </Typography>
        <SelectMulti
          options={LOCATION_OPTIONS(wards) ?? []}
          loading={isFetchingWards}
          selectedOption={SELECTED_LOCATION(filter.locationIds ?? [], wards)}
          handleChange={handleSelectChange('locationIds')}
          placeholder="Select location to filter"
          menuIsOpen={menuIsOpen.location}
          onMenuClose={handleMenuClose('location')}
          openMenuOnClick={handleMenuOpen('location')}
        />
        <div className="mt-4">
          <Typography variant="body-7" color="gray-700" className="mb-2">
            Rack
          </Typography>
          <SelectMulti
            options={RACK_OPTIONS(racks) ?? []}
            loading={isFetchingRacks}
            selectedOption={SELECTED_RACK(filter.rackIds ?? [], racks)}
            handleChange={handleSelectChange('rackIds')}
            placeholder="Select rack to filter"
            menuIsOpen={menuIsOpen.rack}
            onMenuClose={handleMenuClose('rack')}
            openMenuOnClick={handleMenuOpen('rack')}
          />
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply
          </Button>
        </div>
      </Body>
    </Main>
  );
};

export default LocationFilter;
